import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Scene } from '@babylonjs/core/scene';

import { renderFeatures } from '../compat';
import { type Cell, cellKey, cellTexture } from './atlas';

/**
 * The materials the models are built from.
 *
 * These mirror the CSS tokens in `src/styles/tokens.css` — the same tarnished
 * bronze, blood and bone the HUD uses, so the world and the interface look like
 * one game. They cannot literally share the tokens (one is CSS, one is a WebGL
 * uniform), so the values are duplicated here on purpose and the two must be
 * changed together.
 *
 * One instance of each per factory, shared by every model. Materials are the
 * expensive thing to duplicate in Babylon; meshes are cheap.
 */
export type MaterialName =
  | 'steel'
  | 'darkSteel'
  | 'bronze'
  | 'tabard'
  | 'cloth'
  | 'bone'
  | 'boneDark'
  | 'rust'
  | 'ember'
  | 'selfGlow';

interface Spec {
  diffuse: [number, number, number];
  emissive?: [number, number, number];
  specular?: [number, number, number];
}

const SPECS: Record<MaterialName, Spec> = {
  // Crusader — cold plate with warm trim, so the player is the one figure on
  // screen with any gold in them.
  //
  // Everything here is brighter than a lit-room palette would need. The world
  // is deliberately near-black and the post-process quantises on top of that,
  // so a "realistic" dark armour value disappears into the floor entirely. The
  // models have to out-value their surroundings to read as silhouettes.
  steel: { diffuse: [0.52, 0.55, 0.63], emissive: [0.04, 0.045, 0.06], specular: [0.24, 0.25, 0.3] },
  darkSteel: { diffuse: [0.26, 0.27, 0.34], emissive: [0.02, 0.02, 0.03], specular: [0.12, 0.12, 0.16] },
  bronze: { diffuse: [0.66, 0.5, 0.22], emissive: [0.16, 0.11, 0.03], specular: [0.3, 0.24, 0.1] },
  tabard: { diffuse: [0.56, 0.12, 0.14], emissive: [0.12, 0.02, 0.02], specular: [0.04, 0.03, 0.03] },
  cloth: { diffuse: [0.16, 0.16, 0.2], specular: [0.02, 0.02, 0.02] },

  // Skeleton — bone reads cold and slightly green so it never looks like
  // painted armour.
  bone: { diffuse: [0.86, 0.84, 0.73], emissive: [0.08, 0.08, 0.07], specular: [0.08, 0.08, 0.07] },
  boneDark: { diffuse: [0.46, 0.45, 0.38], specular: [0.04, 0.04, 0.04] },
  rust: { diffuse: [0.44, 0.26, 0.17], specular: [0.08, 0.06, 0.05] },

  // Eye sockets and the faint heat in a husk's skull.
  ember: { diffuse: [0.5, 0.12, 0.05], emissive: [0.85, 0.22, 0.05] },

  // A rim of light on the local player so you never lose yourself in a crowd.
  selfGlow: { diffuse: [0.3, 0.55, 0.85], emissive: [0.1, 0.26, 0.45] },
};

/**
 * How a painted cell should be lit.
 *
 * `lit` — an ordinary surface. The cell is the diffuse colour and the sun decides the
 * shading, with the base material's small emissive lift kept so the figure still reads
 * against a near-black floor.
 *
 * `selfLit` — the painting *is* the light. Lighting is switched off for the part and the
 * cell drives emission directly, so what arrives on screen is exactly what was painted.
 * This is for the two cells that were painted as light — the amber eyes and the cyan visor
 * — where an ordinary emissive lift would flood a constant colour over the picture and hide
 * the very thing it was painted for. It does **not** bloom: see `game/glow.ts`'s
 * `NEVER_GLOWS`, where character skin is the first entry and the reason is that a bloomed
 * face turns a character into a lamp.
 */
export type CellLighting = 'lit' | 'selfLit';

/**
 * Where on a curved part a `FRONT` cell lands — Phase 41's answer to *the face is too
 * squared, it needs to be attached to the actual head*.
 *
 * A picture cell mapped plainly onto a sphere is wrapped once round the whole thing, so a
 * face would stretch from ear to ear to the back of the skull. Scaling the texture up and
 * offsetting it puts the picture on the front patch alone; the texture is clamped, so the
 * rest of the surface takes the picture's edge colour — dark skin, dark iron — which is what
 * a head is at the sides anyway, and what the hood or the helm covers. The numbers are in
 * `armour.ts` beside the head that uses them, derived from Babylon's sphere UVs (front is
 * u = 0.75, top is v = 0, measured under `NullEngine`).
 */
export interface CellFit {
  uScale: number;
  vScale: number;
  uOffset: number;
  vOffset: number;
}

function fitKey(f: CellFit): string {
  return `${f.uScale},${f.vScale},${f.uOffset},${f.vOffset}`;
}

/** Creates and caches the shared material set for one scene. */
export class ModelPalette {
  private readonly cache = new Map<MaterialName, StandardMaterial>();
  private readonly textured = new Map<string, StandardMaterial>();

  constructor(private readonly scene: Scene) {}

  get(name: MaterialName): StandardMaterial {
    const existing = this.cache.get(name);
    if (existing) return existing;

    const spec = SPECS[name];
    const mat = new StandardMaterial(`model-${name}`, this.scene);
    mat.diffuseColor = new Color3(...spec.diffuse);
    mat.specularColor = new Color3(...(spec.specular ?? [0.05, 0.05, 0.05]));
    if (spec.emissive) mat.emissiveColor = new Color3(...spec.emissive);
    // Flat, chunky shading suits the pixel post-process better than a tight
    // specular highlight, which the pixel grid turns into a flickering dot.
    mat.specularPower = 16;
    if (!renderFeatures.specular) {
      // Unlit: the emissive term alone still shows the silhouette, so a model
      // stays visible even with lighting stripped out of the shader.
      mat.disableLighting = true;
      mat.emissiveColor = new Color3(...spec.diffuse);
    }
    mat.fogEnabled = renderFeatures.fog;

    this.cache.set(name, mat);
    return mat;
  }

  /**
   * The material for a painted cell, falling back to a flat one.
   *
   * **Three ways this returns the flat material, and all three are normal.** `?textures=0`
   * or safe mode asked for the plainest shader Babylon will compile; the cell has no file
   * in this build; or the caller is on a path that never had art. A texture adds a sampler
   * and a define to the generated material shader, which is the category of change that has
   * taken this world black before — so the way out has to be a switch rather than a rebuild,
   * and every textured part has to have a flat answer ready.
   *
   * `base` is that answer, and it is not only a fallback: its emissive lift and specular are
   * what the textured material inherits, so a cell and the colour it replaced read at the
   * same value against the floor.
   *
   * Cached per cell **and** per base, because the same painting serves different parts at
   * different lifts — the mail cell is a chest under `steel` and a skirt under `cloth`.
   */
  cell(
    c: Cell,
    base: MaterialName,
    lighting: CellLighting = 'lit',
    fit?: CellFit,
  ): StandardMaterial {
    if (!renderFeatures.textures) return this.get(base);

    const key = `${cellKey(c)}|${base}|${lighting}|${fit ? fitKey(fit) : ''}`;
    const existing = this.textured.get(key);
    if (existing) return existing;

    const tex = cellTexture(this.scene, c);
    if (!tex) return this.get(base);
    if (fit) {
      // A `FRONT` cell placed on a *round* part: the picture is scaled onto the patch of the
      // surface that faces the camera and the clamp mode carries its edge colour round the
      // rest. Its own cached material, because the scale lives on the texture.
      tex.uScale = fit.uScale;
      tex.vScale = fit.vScale;
      tex.uOffset = fit.uOffset;
      tex.vOffset = fit.vOffset;
    }

    const spec = SPECS[base];
    const mat = new StandardMaterial(`model-cell-${key}`, this.scene);
    mat.diffuseTexture = tex;
    // White, so the painting arrives as painted. Multiplying by the base's diffuse would
    // darken every cell by whatever colour it is standing in for — and these were painted
    // for a dark game already.
    mat.diffuseColor = new Color3(1, 1, 1);
    mat.specularColor = new Color3(...(spec.specular ?? [0.05, 0.05, 0.05]));
    mat.specularPower = 16;
    if (spec.emissive) mat.emissiveColor = new Color3(...spec.emissive);

    if (lighting === 'selfLit' || !renderFeatures.specular) {
      // The unlit path, for two different reasons that want the same thing: a cell painted
      // as light, and `?specular=0` stripping lighting out of the shader. In both cases the
      // texture has to drive emission or the part goes black — the flat path's equivalent
      // is copying `diffuse` into `emissiveColor`.
      mat.disableLighting = true;
      mat.emissiveTexture = tex;
      mat.emissiveColor = new Color3(1, 1, 1);
    }

    mat.fogEnabled = renderFeatures.fog;

    this.textured.set(key, mat);
    return mat;
  }

  dispose(): void {
    for (const mat of this.cache.values()) mat.dispose();
    this.cache.clear();
    // `true` for the textures: each `cellTexture` call returns its own `Texture` wrapper, so
    // one per material is exactly what there is to release. The decoded bytes behind them are
    // Babylon's URL cache and outlive any one scene, which is the point of sharing them.
    for (const mat of this.textured.values()) mat.dispose(false, true);
    this.textured.clear();
  }
}
